import { useCallback, useEffect, useState } from "react";
import { getAllLanguagesAdmin } from "../httpService";

const DEFAULT_LANGUAGE = { code: "sv", label: "Svenska", enabled: true };

const includeDefaultLanguage = (languages) =>
  languages.some((language) => language.code === DEFAULT_LANGUAGE.code)
    ? languages
    : [DEFAULT_LANGUAGE, ...languages];

export function useTranslationEditor({
  entityId,
  fetchTranslations,
  upsertTranslation,
  emptyForm,
  reinitialize,
  includeDefaultLocale = false,
}) {
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState({});
  const [activeLocale, setActiveLocale] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    getAllLanguagesAdmin()
      .then((langs) => {
        const availableLanguages = includeDefaultLocale
          ? includeDefaultLanguage(langs)
          : langs.filter((l) => l.code !== DEFAULT_LANGUAGE.code);
        setLanguages(availableLanguages);
        setActiveLocale((currentLocale) => {
          if (availableLanguages.length === 0) return null;
          if (availableLanguages.some((language) => language.code === currentLocale)) {
            return currentLocale;
          }
          return availableLanguages[0].code;
        });
      })
      .catch(() => {
        /* ignore */
      });

    fetchTranslations(entityId)
      .then((list) => {
        const map = {};
        for (const t of list) map[t.locale] = t;
        setTranslations(map);
      })
      .catch(() => {
        /* ignore */
      });
  }, [entityId, fetchTranslations, includeDefaultLocale]);

  useEffect(() => {
    if (!activeLocale) return;
    const existing = translations[activeLocale];
    setForm(existing ? { ...existing } : emptyForm());
  }, [activeLocale, translations, emptyForm]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset status when locale changes
  useEffect(() => {
    setStatus(null);
  }, [activeLocale]);

  const updateForm = useCallback((field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
  }, []);

  const handleSave = useCallback(() => {
    if (!(form && activeLocale)) return;
    setSaving(true);
    setStatus(null);
    upsertTranslation(entityId, activeLocale, form)
      .then(() => {
        setTranslations((p) => ({
          ...p,
          [activeLocale]: { ...form, locale: activeLocale },
        }));
        setStatus("success");
        reinitialize();
      })
      .catch(() => setStatus("error"))
      .finally(() => setSaving(false));
  }, [form, activeLocale, entityId, upsertTranslation, reinitialize]);

  return {
    languages,
    translations,
    activeLocale,
    setActiveLocale,
    form,
    updateForm,
    saving,
    status,
    handleSave,
  };
}
